import React from 'react';

import { theme } from '@circleci/compass/dist/theme';
import { css } from '@emotion/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { onClickTrackAction, trackAction } from '~/backplane/analytics';
import Container from '~/components/shared/Container';
import IntlExternalLink from '~/components/shared/IntlExternalLink';
import {
  mediaTabletAndLarger,
  mediaHugeAndLarger,
  mediaMaxWidthSmall,
  huge,
} from '~/components/shared/viewPorts';
import { outer } from '~/globalStyles';
import { styled } from '~/styled';

const BannerBg = styled.div`
  position: relative;
  padding: ${theme.space.space8} 0px;
  color: ${theme.colors.white};
  background-color: #3b6385;
  overflow: hidden;
  box-sizing: border-box;
  max-height: 300px;
  transition:
    max-height 500ms ease-out,
    padding 300ms ease-out;

  ${mediaTabletAndLarger} {
    transition:
      max-height 250ms ease-out,
      padding 250ms ease-out;
  }

  .banner-closing & {
    max-height: 0;
    padding: 0;
  }
`;

const FlexDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  ${mediaMaxWidthSmall} {
    flex-wrap: wrap;
  }
`;

const P = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.fontsize14};
  font-weight: ${theme.fontWeights.regular};
  color: inherit;
  line-height: ${theme.fontSizes.fontsize20};
  align-self: center;
  ${mediaTabletAndLarger} and (max-width: ${huge + 80}px) {
    padding-right: 15px;
  }
`;

const Lede = styled.p`
  flex: 0 0 auto;
  padding: 0 12px;
  margin: 0 14px 0 0;
  color: inherit;
  font-weight: ${theme.fontWeights.medium};
  font-size: 12px;
  line-height: 2.5;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  background-color: rgba(0, 0, 0, 0.15);

  ${mediaMaxWidthSmall} {
    margin-bottom: 8px;
  }
`;

const Br = styled.br`
  ${mediaTabletAndLarger} {
    display: none;
  }
`;

const HugeOnly = styled.span`
  display: none;
  ${mediaHugeAndLarger} {
    display: inline;
  }
`;

const ctaLink = css`
  &,
  &:visited {
    color: ${theme.colors.white};
    font-weight: ${theme.fontWeights.medium};
  }

  &:hover {
    -webkit-text-stroke: ${outer.textStroke};
  }

  &:after {
    content: '\\25B8';
    padding-left: 2px;
    -webkit-text-stroke: 0;
  }
`;

const Close = styled.button`
  position: absolute;
  top: 1.5em;
  right: 15px;
  width: ${theme.space.space40};
  height: ${theme.space.space40};
  padding: 0px;
  border: 0px;
  background: transparent;
  cursor: pointer;
  transform: translate(13px, -50%);
  -webkit-appearance: none;

  &:focus {
    outline: 0;
    background: radial-gradient(
      circle at center,
      rgba(0, 0, 0, 0.15) 0,
      rgba(0, 0, 0, 0.15) 50%,
      transparent 51%
    );
  }

  ${mediaTabletAndLarger} {
    top: 50%;
  }

  @media (min-width: ${huge + 80}px) {
    right: 43px;
  }

  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    height: 2px;
    width: 17px;
    background-color: ${theme.colors.white};
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

interface BannerProps {
  bannerId: string;
  closeBanner?: () => void;
  bannerHidden: boolean;
}

export const headerOffsetMobile = 96;
export const headerOffset = 142;
export const bannerOffset = headerOffset + 45;

export const Banner = ({
  bannerId,
  closeBanner,
  bannerHidden,
}: BannerProps) => {
  const intl = useIntl();
  const tabIndex = bannerHidden ? -1 : 0;
  const bannerName = bannerId.split('-').join(' ');

  const onClick = () => {
    trackAction('click-outer-cta', {
      cta_text: 'x',
      page_location: `Dev Hub Banner for ${bannerName}`,
    });
    closeBanner && closeBanner();
  };

  return (
    <BannerBg
      aria-hidden={bannerHidden}
      role={bannerHidden ? 'presentation' : 'banner'}
    >
      <Container>
        <FlexDiv>
          <Lede>{intl.formatMessage({ id: `Banner.${bannerId}.lede` })}</Lede>
          <Br />
          <P>
            <FormattedMessage
              id={`Banner.${bannerId}.body`}
              defaultMessage="Private orbs now available on the CircleCI Scale plan. <cta>Read More</cta>"
              values={{
                cta: (chunks) => {
                  return (
                    <IntlExternalLink
                      href={intl.formatMessage({
                        id: `Banner.${bannerId}.ctaLink`,
                      })}
                      customStyles={ctaLink}
                      tabIndex={tabIndex}
                      onClick={onClickTrackAction({
                        name: 'click-outer-cta',
                        props: {
                          page_location: 'Dev Hub Banner',
                          category: bannerName,
                        },
                      })}
                    >
                      {chunks}
                    </IntlExternalLink>
                  );
                },
                huge: (chunks) => {
                  return <HugeOnly>{chunks}</HugeOnly>;
                },
                note: (chunks) => {
                  return <small>{chunks}</small>;
                },
              }}
            />
          </P>
        </FlexDiv>
      </Container>
      {closeBanner && (
        <Close data-testid="banner-close" onClick={onClick} tabIndex={tabIndex}>
          <SrOnly>{intl.formatMessage({ id: 'Banner.close' })}</SrOnly>
        </Close>
      )}
    </BannerBg>
  );
};
